import { AtendimentoEstado } from '../../../../../../_metronic/helpers'
import { PessoaList } from '../../../../auth/core/_models'
import { AtendimentoView } from '../../../atendimento/core/_models'
import { FormularioAtendimentoList } from '../../../avaliacoes/avaliacoes-pendentes-list/core/_models'
import {
  formulariosEmAberto,
  pareceresParaAssinar,
  solicitacaoRespondida,
  temFormularioMarcado,
} from '../../../pareceres/pareceres-list/core/formulariosInterconsulta'

/** O que a coluna do parecer mostra; só `responder` abre o formulário. */
export type EtapaParecer =
  | { tipo: 'cancelada' }
  | { tipo: 'finalizada' }
  /** Finalizada pelo teleconsultor logado, com parecer respondido e ainda não assinado. */
  | { tipo: 'aguardandoAssinatura' }
  | { tipo: 'pendente' }
  | { tipo: 'somenteLeitura' }
  | { tipo: 'solicitacaoIncompleta' }
  | { tipo: 'semFormulario' }
  /** Ainda não é do teleconsultor logado; `responsavelAtual` quando outro está atendendo. */
  | { tipo: 'assumir'; responsavelAtual?: PessoaList }
  /** Mais de um formulário em aberto e nenhum marcado pelo backend (interconsulta antiga). */
  | { tipo: 'selecionarFormulario'; formularios: FormularioAtendimentoList[] }
  | { tipo: 'responder'; formulario: FormularioAtendimentoList }

type Entrada = {
  atendimento: AtendimentoView
  /** Pessoa do teleconsultor logado. */
  pessoaId: number
  /** Tem vínculo médico ativo na instituição do paciente. */
  podeResponder: boolean
  /** Formulário escolhido na etapa `selecionarFormulario`. */
  escolhido?: FormularioAtendimentoList
}

/**
 * Decide a etapa pelo estado do atendimento. A réplica devolve o atendimento a "Aguardando" com uma
 * nova instância do formulário, então segue o mesmo caminho de uma interconsulta nova.
 */
export const etapaDoParecer = ({
  atendimento,
  pessoaId,
  podeResponder,
  escolhido,
}: Entrada): EtapaParecer => {
  const estado = atendimento.estado?.id

  if (estado === AtendimentoEstado.Cancelado) return { tipo: 'cancelada' }
  if (estado === AtendimentoEstado.Finalizado) {
    // O backend só aceita a assinatura do responsável pelo atendimento.
    const assinaturaPendente =
      atendimento.responsavel?.id === pessoaId &&
      pareceresParaAssinar(atendimento.formularios).length > 0
    return { tipo: assinaturaPendente ? 'aguardandoAssinatura' : 'finalizada' }
  }
  if (estado === AtendimentoEstado.Pendente || estado === AtendimentoEstado.RetornoPendencia) {
    return { tipo: 'pendente' }
  }
  if (!podeResponder) return { tipo: 'somenteLeitura' }
  if (!solicitacaoRespondida(atendimento.formularios)) return { tipo: 'solicitacaoIncompleta' }

  const abertos = formulariosEmAberto(atendimento.formularios)
  if (!abertos.length) return { tipo: 'semFormulario' }

  const emAtendimento = estado === AtendimentoEstado.EmAtendimento
  if (!emAtendimento || atendimento.responsavel?.id !== pessoaId) {
    return {
      tipo: 'assumir',
      responsavelAtual: emAtendimento ? atendimento.responsavel : undefined,
    }
  }

  const formulario =
    escolhido ??
    (abertos.length === 1 || temFormularioMarcado(atendimento.formularios) ? abertos[0] : undefined)
  return formulario
    ? { tipo: 'responder', formulario }
    : { tipo: 'selecionarFormulario', formularios: abertos }
}
